import { markSiteCollected, mergeVisits } from './store';
import type { Site, Visit } from './types';

export type SiteMetrics = { pv: number; ip: number; uv: number };

export type SiteSummary = {
  id?: string;
  domain?: string;
  trackingCode: string;
  lastCollectAt?: string | null;
  today: SiteMetrics;
  yesterday: SiteMetrics;
};

const EMPTY_METRICS: SiteMetrics = { pv: 0, ip: 0, uv: 0 };

export async function fetchRemoteVisits(trackingCode: string, token?: string): Promise<Visit[]> {
  try {
    const qs = new URLSearchParams({ code: trackingCode });
    if (token) qs.set('token', token);
    const res = await fetch(`/api/visits?${qs.toString()}`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.visits) ? (data.visits as Visit[]) : [];
  } catch {
    return [];
  }
}

export function mapSiteVisits(site: Site, visits: Visit[]): Visit[] {
  return visits.map((v) => ({
    ...v,
    siteId: site.id,
    trackingCode: site.trackingCode,
  }));
}

export async function fetchSiteOverview(token: string, code: string): Promise<{ today: SiteMetrics; yesterday: SiteMetrics } | null> {
  try {
    const res = await fetch(
      `/api/overview?token=${encodeURIComponent(token)}&code=${encodeURIComponent(code)}`
    );
    if (!res.ok) return null;
    const data = (await res.json()) as { today?: SiteMetrics; yesterday?: SiteMetrics };
    if (!data.today || !data.yesterday) return null;
    return { today: data.today, yesterday: data.yesterday };
  } catch {
    return null;
  }
}

export async function fetchSiteSummaries(token: string, sites: Site[] = []): Promise<SiteSummary[]> {
  try {
    const res = await fetch(`/api/summaries?token=${encodeURIComponent(token)}`);
    if (res.ok) {
      const data = (await res.json()) as { items?: SiteSummary[] };
      if (Array.isArray(data.items) && data.items.length) return data.items;
    }
  } catch {
    /* 旧接口没有 summaries 时走单站 overview */
  }
  const items = await Promise.all(
    sites.map(async (site) => {
      const overview = await fetchSiteOverview(token, site.trackingCode);
      return {
        id: site.id,
        domain: site.domain,
        trackingCode: site.trackingCode,
        lastCollectAt: site.lastCollectAt,
        today: overview?.today || EMPTY_METRICS,
        yesterday: overview?.yesterday || EMPTY_METRICS,
      };
    })
  );
  return items;
}

export async function syncRemoteVisits(site: Site, token?: string): Promise<number> {
  const visits = mapSiteVisits(site, await fetchRemoteVisits(site.trackingCode, token));
  mergeVisits(visits);
  if (visits.length && (site.status === 'pending' || !site.lastCollectAt)) {
    markSiteCollected(site.id);
  }
  return visits.length;
}
